import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import posthog from 'posthog-js'
import moment from 'jalali-moment'
import { toast } from 'react-toastify'
import { FiChevronLeft } from 'react-icons/fi'
import { LuInbox, LuTrash2, LuUserPlus } from 'react-icons/lu'
import Header from 'components/headers/funds'
import SideBar from 'components/sidebar'
import SideBarItems from 'components/sidebar/items'
import CustomBottomSheet from 'components/bottomSheet'
import { SectionError } from 'components/states'
import { getBoxes } from 'services/funds'
import { deleteMessage, makeMessagesSeen } from 'services/messages'
import { faPlain } from 'utilities/helper/format'
import useGetRequestMessage, {
    MESSAGES_QUERY_KEY,
} from './hooks/useGetRequestMessage'
import useGetCashboxMessages from './hooks/useGetCashboxMessages'
import MessageCard, { MessageCardSkeleton } from './item'
import {
    MessageTarget,
    groupMessagesByDay,
    localDayKey,
    resolveMessageTarget,
} from './messageTarget'

type Message = IRequestMessageResultMessage
type Pages = { pages: IRequestMessage[]; pageParams: unknown[] }

/** Android marks the inbox seen a second after the tab opens; so do we. */
const MARK_SEEN_DELAY_MS = 1200

const updateMessages = (
    data: Pages | undefined,
    change: (messages: Message[]) => Message[]
): Pages | undefined =>
    data && {
        ...data,
        pages: data.pages.map((page) => ({
            ...page,
            result: page.result.map((day) => ({
                ...day,
                messages: change(day.messages),
            })),
        })),
    }

const dayLabel = (key: string) => {
    const now = Math.floor(Date.now() / 1000)
    if (key === localDayKey(now)) return 'امروز'
    if (key === localDayKey(now - 86_400)) return 'دیروز'
    const day = moment.from(key, 'en', 'YYYY-MM-DD').locale('fa')
    const sameYear =
        day.format('jYYYY') === moment().locale('fa').format('jYYYY')
    return day.format(sameYear ? 'dddd D MMMM' : 'dddd D MMMM YYYY')
}

/**
 * «پیام‌ها» — the member's inbox.
 *
 * Every message used to be a grey-on-white paragraph with an `HH:mm:ss`
 * stamp, and tapping one did nothing: the PWA never implemented Android's
 * `doAction`. A reminder to pay could not take you to the payment, and a
 * message could not be deleted.
 *
 * Now each message says which fund it is about, carries an action row that
 * goes where Android's tap went (resolved against the member's own funds, see
 * `messageTarget.ts`), and can be deleted. The inbox is marked seen the way
 * Android does it — one call with the newest id, a moment after opening —
 * while the unread highlight stays on screen for the rest of the visit, so the
 * member can still see what was new.
 *
 * `['get-boxes']` is shared with the home page, so resolving fund names costs
 * no extra request for anyone who came from there.
 */
export default function MessagesPage() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const {
        requestMessage,
        isLoading,
        isError,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetRequestMessage()
    const { cashboxData } = useGetCashboxMessages()
    const { data: boxes } = useQuery(['get-boxes'], getBoxes, {
        staleTime: 60_000,
    })

    const [hidden, setHidden] = useState<ReadonlySet<number>>(() => new Set())
    const [menuFor, setMenuFor] = useState<Message | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const days = useMemo(
        () => groupMessagesByDay(requestMessage?.pages, hidden),
        [requestMessage, hidden]
    )
    const messages = useMemo(() => days.flatMap((day) => day.messages), [days])

    // Everything that arrived unseen stays highlighted for this visit, even
    // after the server (and the cache) have marked it seen. Adding ids is
    // idempotent, so doing it while rendering is safe.
    const unreadRef = useRef(new Set<number>())
    for (const message of messages) {
        if (!message.is_seen) unreadRef.current.add(message.id)
    }
    const unreadCount = messages.filter((m) =>
        unreadRef.current.has(m.id)
    ).length

    const targets = useMemo(() => {
        const map = new Map<number, MessageTarget>()
        for (const message of messages)
            map.set(message.id, resolveMessageTarget(message, boxes))
        return map
    }, [messages, boxes])

    const inviteCount = cashboxData?.results?.length ?? 0

    // react-query 3 has no `isFetchNextPageError`: a failed next page leaves
    // the loaded pages in place and flips the whole query to `error`.
    const loaded = Boolean(requestMessage)
    const firstPageFailed = isError && !loaded
    const nextPageFailed = isError && loaded

    // --- mark seen -------------------------------------------------------
    const newestUnseenId = useMemo(
        () =>
            messages.reduce(
                (max, m) => (!m.is_seen && m.id > max ? m.id : max),
                0
            ),
        [messages]
    )
    useEffect(() => {
        if (!newestUnseenId) return
        const timer = window.setTimeout(() => {
            makeMessagesSeen(newestUnseenId)
                .then(() =>
                    // The server marked this id *and everything older*.
                    queryClient.setQueryData<Pages | undefined>(
                        MESSAGES_QUERY_KEY,
                        (data) =>
                            updateMessages(data, (list) =>
                                list.map((m) =>
                                    m.id <= newestUnseenId
                                        ? { ...m, is_seen: true }
                                        : m
                                )
                            )
                    )
                )
                .catch(() => {
                    // Not worth a toast: the messages were read either way,
                    // and the next visit simply tries again.
                })
        }, MARK_SEEN_DELAY_MS)
        return () => window.clearTimeout(timer)
    }, [newestUnseenId, queryClient])

    // --- analytics --------------------------------------------------------
    const viewedRef = useRef(false)
    useEffect(() => {
        if (viewedRef.current || !requestMessage) return
        viewedRef.current = true
        posthog.capture('messages_viewed', {
            message_count: messages.length,
            unread_count: unreadCount,
            invite_count: cashboxData ? inviteCount : null,
        })
        // Once per visit, on the first page only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestMessage])

    // --- infinite scroll --------------------------------------------------
    const sentinel = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        const node = sentinel.current
        if (!node || !hasNextPage) return
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0]?.isIntersecting &&
                    !isFetchingNextPage &&
                    !nextPageFailed
                )
                    fetchNextPage()
            },
            // Ask for the next page a screen early, so the member rarely
            // sees it load.
            { rootMargin: '0px 0px 600px 0px' }
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [hasNextPage, isFetchingNextPage, nextPageFailed, fetchNextPage])

    // --- actions ----------------------------------------------------------
    const openMessage = useCallback(
        (message: Message, target: MessageTarget) => {
            const destination = target.destination
            if (!destination) return
            posthog.capture('message_opened', {
                action: message.action,
                kind: target.kind,
                box_resolved: Boolean(target.boxName),
                was_unread: unreadRef.current.has(message.id),
            })
            if (destination.type === 'route') {
                navigate(destination.path)
                return
            }
            const { href } = destination
            if (href.startsWith('tel:')) {
                window.location.href = href
                return
            }
            // A link to ourselves stays inside the app instead of opening a
            // second copy of it in a new tab.
            const url = new URL(href)
            if (url.origin === window.location.origin) {
                navigate(`${url.pathname}${url.search}${url.hash}`)
                return
            }
            window.open(href, '_blank', 'noopener,noreferrer')
        },
        [navigate]
    )

    const openMenu = useCallback((message: Message) => {
        setMenuFor(message)
        setMenuOpen(true)
    }, [])

    const confirmDelete = async () => {
        if (!menuFor || deleting) return
        const message = menuFor
        setDeleting(true)
        try {
            await deleteMessage(message.id)
            setHidden((prev) => new Set(prev).add(message.id))
            queryClient.setQueryData<Pages | undefined>(
                MESSAGES_QUERY_KEY,
                (data) =>
                    updateMessages(data, (list) =>
                        list.filter((m) => m.id !== message.id)
                    )
            )
            posthog.capture('message_deleted', {
                action: message.action,
                kind: targets.get(message.id)?.kind,
            })
            setMenuOpen(false)
        } catch {
            toast.error('حذف پیام انجام نشد. دوباره تلاش کنید.')
        } finally {
            setDeleting(false)
        }
    }

    const openInvites = () => {
        posthog.capture('messages_invites_opened', { count: inviteCount })
        navigate('/cashbox/invitations')
    }

    // --- render -----------------------------------------------------------
    return (
        <>
            <Header title="پیام‌ها" />
            <SideBar>
                <SideBarItems />
            </SideBar>

            <div className="min-h-screen bg-canvas">
                <div className="flex flex-col gap-3 px-3 pb-6 pt-4">
                    {inviteCount > 0 && (
                        <button
                            type="button"
                            onClick={openInvites}
                            className="flex w-full items-center gap-3 rounded-card border border-divider bg-white p-4 text-right shadow-badge transition-colors animate-fade-in active:bg-sunken"
                        >
                            <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-deep">
                                <LuUserPlus className="h-5 w-5" />
                                <span className="absolute -left-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none text-white ring-2 ring-white">
                                    {faPlain.format(inviteCount)}
                                </span>
                            </span>
                            <span className="flex-1 overflow-hidden">
                                <span className="block text-sm font-bold text-heading">
                                    دعوت‌ها و درخواست‌های عضویت
                                </span>
                                <span className="mt-1 block text-xs text-text-secondary">
                                    {faPlain.format(inviteCount)} مورد منتظر
                                    پاسخ شماست
                                </span>
                            </span>
                            <FiChevronLeft className="h-5 w-5 shrink-0 text-text-muted" />
                        </button>
                    )}

                    {loaded && messages.length > 0 && (
                        <div className="flex items-center justify-between px-1 pt-1">
                            <h2 className="text-base font-bold text-heading">
                                پیام‌های شما
                            </h2>
                            {unreadCount > 0 && (
                                <span className="rounded-full bg-brand-soft px-2.5 py-1 text-xs font-bold text-brand-deep">
                                    {faPlain.format(unreadCount)} پیام تازه
                                </span>
                            )}
                        </div>
                    )}

                    {isLoading && (
                        <>
                            <MessageCardSkeleton />
                            <MessageCardSkeleton />
                            <MessageCardSkeleton />
                        </>
                    )}

                    {firstPageFailed && (
                        <SectionError
                            message="دریافت پیام‌ها انجام نشد"
                            onRetry={refetch}
                        />
                    )}

                    {loaded && messages.length === 0 && <EmptyInbox />}

                    {days.map((day) => (
                        <section key={day.key} className="flex flex-col gap-3">
                            <div className="pointer-events-none sticky top-14 z-10 flex justify-center pt-1">
                                <span className="rounded-full border border-divider bg-white/95 px-3 py-1 text-xs text-text-secondary shadow-badge backdrop-blur">
                                    {dayLabel(day.key)}
                                </span>
                            </div>
                            {day.messages.map((message) => (
                                <MessageCard
                                    key={message.id}
                                    message={message}
                                    target={
                                        targets.get(message.id) ?? {
                                            kind: 'info',
                                        }
                                    }
                                    unread={unreadRef.current.has(message.id)}
                                    onOpen={openMessage}
                                    onMenu={openMenu}
                                />
                            ))}
                        </section>
                    ))}

                    {isFetchingNextPage && <MessageCardSkeleton />}

                    {nextPageFailed && !isFetchingNextPage && (
                        <SectionError
                            message="دریافت پیام‌های قدیمی‌تر انجام نشد"
                            onRetry={() => fetchNextPage()}
                        />
                    )}

                    {loaded &&
                        !nextPageFailed &&
                        messages.length > 0 &&
                        !hasNextPage &&
                        !isFetchingNextPage && (
                            <div className="flex items-center gap-3 px-6 pt-2 text-xs text-text-muted">
                                <span className="h-px flex-1 bg-stroke" />
                                پیام قدیمی‌تری نیست
                                <span className="h-px flex-1 bg-stroke" />
                            </div>
                        )}

                    <div ref={sentinel} aria-hidden />
                </div>
            </div>

            <CustomBottomSheet
                isOpen={menuOpen}
                onDismiss={() => !deleting && setMenuOpen(false)}
            >
                {menuFor && (
                    <div className="pb-1">
                        <p className="text-center text-base font-bold text-heading">
                            حذف این پیام؟
                        </p>
                        <p className="mt-3 line-clamp-3 whitespace-pre-line break-words rounded-card border border-divider bg-sunken p-3 text-sm leading-7 text-text-body">
                            {menuFor.body}
                        </p>
                        <p className="mt-2 text-center text-xs text-text-secondary">
                            پیام حذف‌شده قابل بازگرداندن نیست.
                        </p>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            disabled={deleting}
                            aria-busy={deleting}
                            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-card bg-danger text-sm font-bold text-white transition-opacity active:opacity-90 disabled:opacity-60"
                        >
                            <LuTrash2 className="h-[18px] w-[18px]" />
                            {deleting ? 'در حال حذف…' : 'حذف پیام'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setMenuOpen(false)}
                            disabled={deleting}
                            className="mt-2 h-12 w-full rounded-card text-sm font-bold text-text-secondary transition-colors active:bg-divider"
                        >
                            انصراف
                        </button>
                    </div>
                )}
            </CustomBottomSheet>
        </>
    )
}

function EmptyInbox() {
    return (
        <div className="flex flex-col items-center px-8 pt-16 text-center animate-fade-in">
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-brand-soft">
                <span className="absolute inset-3 rounded-full bg-white/70" />
                <LuInbox className="relative h-10 w-10 text-green-1" />
            </div>
            <p className="mt-5 text-base font-bold text-heading">
                هنوز پیامی ندارید
            </p>
            <p className="mt-2 text-sm leading-7 text-text-secondary">
                یادآوری پرداخت‌ها، دعوت به صندوق‌ها و خبرهای صندوق‌هایتان اینجا
                جمع می‌شود.
            </p>
        </div>
    )
}
